// src/components/StudentProfile.js
import React, { useEffect, useState, useRef } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  doc as docRef,
  getDoc,
  deleteDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import '../index.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function StudentProfile({ student, onBack }) {
  const [info, setInfo]               = useState(null);
  const [attendance, setAttendance]   = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showAtt, setShowAtt]         = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [editing, setEditing]         = useState(false);
  const [editData, setEditData]       = useState(null);
  const [pdfBlob, setPdfBlob]         = useState(null);
  const refDiv                        = useRef();
  const chartRef                      = useRef();

  useEffect(() => {
    (async () => {
      const sSnap = await getDoc(docRef(db, 'students', student.id));
      if (sSnap.exists()) {
        const d = sSnap.data();
        setInfo(d);
        setEditData({
          name: d.name || '',
          email: d.email || '',
          mobile: d.mobile || '',
          class: d.class || '',
          school: d.school || '',
          father_name: d.father_name || '',
          father_phone: d.father_phone || '',
          mother_name: d.mother_name || '',
          mother_phone: d.mother_phone || '',
          date_of_joining: d.date_of_joining
            ? (d.date_of_joining.toDate
                ? d.date_of_joining.toDate().toISOString().slice(0,10)
                : new Date(d.date_of_joining).toISOString().slice(0,10))
            : '',
          total_fees: d.total_fees ?? ''
        });
      }
      const attSnap = await getDocs(
        query(collection(db, 'attendance'), where('student_id','==',student.id))
      );
      setAttendance(attSnap.docs.map(d => d.data()));
      const trSnap = await getDocs(
        query(collection(db, 'testResults'), where('student_id','==',student.id))
      );
      const raw = trSnap.docs.map(d => d.data());
      const ids = [...new Set(raw.map(r => r.test_id))];
      const metas = await Promise.all(ids.map(id => getDoc(docRef(db,'tests',id))));
      const topicMap = {}, fmMap = {};
      metas.forEach(m => {
        if (m.exists()) {
          const dd = m.data();
          topicMap[m.id] = dd.topic;
          fmMap[m.id]    = dd.full_marks;
        }
      });
      setTestResults(
        raw
          .map(r => ({
            ...r,
            topic:      topicMap[r.test_id]   || 'Unknown',
            full_marks: fmMap[r.test_id]      || 0
          }))
          .sort((a,b) => b.marks - a.marks)
      );
      setLoading(false);
    })();
  }, [student.id]);

  if (loading || !info || !editData) return <p>Loading…</p>;

  const handleDelete = async () => {
    const attSnap = await getDocs(
      query(collection(db,'attendance'), where('student_id','==',student.id))
    );
    await Promise.all(attSnap.docs.map(d => deleteDoc(docRef(db,'attendance',d.id))));
    const trSnap = await getDocs(
      query(collection(db,'testResults'), where('student_id','==',student.id))
    );
    await Promise.all(trSnap.docs.map(d => deleteDoc(docRef(db,'testResults',d.id))));
    await deleteDoc(docRef(db,'students',student.id));
    onBack();
  };
  const handleSave = async () => {
    const ref = docRef(db,'students',student.id);
    const payload = { ...editData };
    if (editData.date_of_joining) {
      const d = new Date(editData.date_of_joining);
      if (!isNaN(d)) payload.date_of_joining = d;
    }
    if (editData.total_fees !== '') {
      const f = Number(editData.total_fees);
      if (!isNaN(f)) payload.total_fees = f;
    }
    await updateDoc(ref, payload);
    setInfo(prev => ({ ...prev, ...payload }));
    setEditing(false);
  };

  const total   = attendance.length;
  const present = attendance.filter(a => a.status.startsWith('present')).length;
  const absents = attendance.filter(a => a.status === 'absent').length;
  const percent = total ? Math.round((present/total)*100) : 0;

  let displayDateJoining = 'N/A';
  if (info.date_of_joining) {
    const dt = info.date_of_joining.toDate
      ? info.date_of_joining.toDate()
      : new Date(info.date_of_joining);
    displayDateJoining = dt.toLocaleDateString('en-GB', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
  }

  return (
    <div ref={refDiv} className="p-6 bg-white rounded shadow max-w-md mx-auto space-y-4">
      <div className="profile-header">
        <button onClick={onBack} className="text-blue-600 underline">← Back</button>
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">{info.name}</h2>
          {!editing ? (
            <div className="flex gap-2">
              <button onClick={() => setEditing(true)} className="px-3 py-1 bg-yellow-600 text-white rounded">Edit</button>
              <button onClick={handleDelete} className="px-3 py-1 bg-red-600 text-white rounded">Delete</button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button onClick={handleSave} className="px-3 py-1 bg-green-600 text-white rounded">Save</button>
              <button onClick={() => setEditing(false)} className="px-3 py-1 bg-gray-300 rounded">Cancel</button>
            </div>
          )}
        </div>
        {!editing && (
          <>
            <div>Class {info.class}, {info.school}</div>
            <div>Batch: {student.batchNames?.join(', ') || 'N/A'}</div>
            <div>Mobile: {info.mobile} | Email: {info.email}</div>
            <div>
              Parent: {info.father_name} ({info.father_phone})
              {info.mother_name ? `, ${info.mother_name} (${info.mother_phone})` : ''}
            </div>
            <div>Date of Joining: {displayDateJoining}</div>
            <div>Total Fees: ₹{info.total_fees ?? ''}</div>
          </>
        )}
      </div>
      {/* Edit Mode */}
       {editing && (
        <div className="space-y-4">
          {[
            ['Name','name','text'],
            ['Email','email','email'],
            ['Mobile','mobile','text'],
            ['Class','class','text'],
            ['School','school','text'],
            ['Father Name','father_name','text'],
            ['Father Phone','father_phone','text'],
            ['Mother Name','mother_name','text'],
            ['Mother Phone','mother_phone','text'],
            ['Join Date','date_of_joining','date'],
            ['Total Fees','total_fees','number']
          ].map(([label,key,type]) => (
            <div key={key}>
              <label className="block text-sm font-medium">{label}</label>
              <input
                type={type}
                value={editData[key]}
                onChange={e => setEditData(ed => ({ ...ed, [key]: e.target.value }))}
                className="w-full px-2 py-1 border rounded"
              />
            </div>
          ))}
        </div>
      )}
      {/* Attendance Report */}
      <div className="attendance-section">
        <h3 className="text-xl font-semibold">Attendance Report</h3>
        <div>
          Total: {total} | Present: {present} | Absents: {absents} | %: {percent}%
          <button onClick={() => setShowAtt(!showAtt)} className="text-blue-600 underline ml-2">View Details</button>
        </div>
        {showAtt && (
          <div className="mt-2 space-y-2">
            <div>
              <label className="mr-2">Status:</label>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="px-2 py-1 border rounded"
              >
                <option value="all">All</option>
                <option value="present_online">Online</option>
                <option value="present_offline">Offline</option>
                <option value="absent">Absent</option>
              </select>
            </div>
            <ul className="list-disc list-inside space-y-1">
              {attendance
                .filter(a => filterStatus === 'all' ? true : a.status === filterStatus)
                .map((a,i) => (
                  <li key={i}>
                    {a.date} – {a.topic} – {a.status.replace('_',' ')}
                    {a.status==='absent'&&a.reason?` (Reason: ${a.reason})` : ''}
                  </li>
                ))
              }
            </ul>
          </div>
        )}
      </div>

      {/* Test Report */}
      <div className="test-graph">
        <h3 className="text-xl font-semibold">Test Report</h3>
        <div style={{ position: 'relative', width: '100%' }}>
          <Bar
            ref={chartRef}
            data={{
              labels: testResults.map(r => `${r.topic} (FM:${r.full_marks})`),
              datasets: [{
                data: testResults.map(r => r.marks),
                backgroundColor: testResults.map(() => 'rgba(54,162,235,0.6)')
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false } }
            }}
          />
        </div>
      </div>

      {/* Send Report */}
      <button
        onClick={async () => {
          // 1) Expand attendance if needed
          if (!showAtt) {
            setShowAtt(true);
            await new Promise(res => setTimeout(res, 300));
          }

          // 2) Swap Chart.js canvas → <img> for mobile compatibility
          const canvasEl = chartRef.current?.canvas;
          let chartImg;
          if (canvasEl) {
            const dataUrl = chartRef.current.toBase64Image();
            chartImg = document.createElement('img');
            chartImg.src = dataUrl;
            chartImg.style.width = '100%';
            canvasEl.parentNode.replaceChild(chartImg, canvasEl);
          }

          // 3) Inject logo into profile-header for PDF
          const headerEl = refDiv.current.querySelector('.profile-header');
          const logo = document.createElement('img');
          logo.src = '/logo.png';
          logo.crossOrigin = 'anonymous';
          logo.style.width = '150px';
          logo.style.height = 'auto';
          logo.style.display = 'block';
          logo.style.margin = '0 auto 16px';
          headerEl.prepend(logo);
          await new Promise(res => (logo.complete ? res() : (logo.onload = res)));

          // 4) Hide all buttons
          const buttons = Array.from(refDiv.current.querySelectorAll('button'));
          buttons.forEach(b => (b.style.display = 'none'));

          // 5) Build PDF
          const pdf = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
          const opts = { scale: 2, useCORS: true, allowTaint: false };
          const pageW = pdf.internal.pageSize.getWidth();

          // Page 1: Header
          {
            const cnv = await html2canvas(headerEl, opts);
            const img = cnv.toDataURL('image/png');
            const h   = (cnv.height * pageW) / cnv.width;
            pdf.addImage(img, 'PNG', 0, 0, pageW, h);
          }
          pdf.addPage();

          // Page 2: Attendance
          {
            const attEl = refDiv.current.querySelector('.attendance-section');
            const cnv   = await html2canvas(attEl, opts);
            const img   = cnv.toDataURL('image/png');
            const h     = (cnv.height * pageW) / cnv.width;
            pdf.addImage(img, 'PNG', 0, 0, pageW, h);
          }
          pdf.addPage();

          // Page 3: Test Report
          {
            const graphEl = refDiv.current.querySelector('.test-graph');
            const cnv     = await html2canvas(graphEl, opts);
            const img     = cnv.toDataURL('image/png');
            const h       = (cnv.height * pageW) / cnv.width;
            pdf.addImage(img, 'PNG', 0, 0, pageW, h);
          }

          // 6) Download & stash blob
          const blob = pdf.output('blob');
          setPdfBlob(blob);
          pdf.save(`${info.name}-report.pdf`);

          // 7) Cleanup
          logo.remove();
          buttons.forEach(b => (b.style.display = ''));
          if (chartImg && canvasEl) {
            chartImg.parentNode.replaceChild(canvasEl, chartImg);
          }
        }}
        className="px-4 py-2 bg-green-600 text-white rounded"
      >
        Send Report
      </button>

      {/* Share Report */}
      {pdfBlob && (
        <button
          onClick={() => {
            const file = new File(
              [pdfBlob],
              `${info.name}-report.pdf`,
              { type: 'application/pdf' }
            );
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
              navigator.share({ files: [file], title: `${info.name} Report` });
            } else {
              alert('Sharing not supported on this device.');
            }
          }}
          className="ml-2 px-4 py-2 bg-blue-600 text-white rounded"
        >
          Share Report
        </button>
      )}
    </div>
  );
}
